<template>
  <div>
    <div class="ui form">
      <Loader v-if="!selectedList || !itineraryListMutable"></Loader>
      <div v-else>
        <h1 style="display: inline; margin-right: 25px">
          Adventure Wall - <span v-if="selectedList">{{ selectedList.name }}</span>
        </h1>
        <div class="settings">
          <div class="ui stackable grid">
            <div class="ui four wide column settings-item-wrapper">
              <div class="ui toggle checkbox top">
                <input type="checkbox" v-model="selectedList.includeAllAdventures" />
                <label><b>Include All Adventures</b></label>
              </div>
              <div>
                <div class="ui toggle checkbox">
                  <input type="checkbox" v-model="selectedList.randomize" />
                  <label><b>Randomize Adventures</b></label>
                </div>
              </div>
            </div>
            <div class="ui three wide column">
              <div class="sub-section-title"><b>Max to Show</b> (Optional)</div>
              <div class="ui input">
                <input v-model="selectedList.maxCount" type="number" />
              </div>
            </div>
            <div class="ui eight wide column">
              <label><b>Name</b> (not shown anywhere, it's just for your reference)</label>
              <input v-if="selectedList" class="ui input text-field" style="width: 350px" type="text" v-model="selectedList.name" />
              <p class="cms"></p>
            </div>
          </div>
          <div class="override-urls-link-wrap">
            <SrpButton fill="outlined" color="gray" size="tiny" @click="showOverrideUrlsModal = true">
              <template #icon><IconEmbedded name="link_2-5" :size="16" /></template>
              Override Urls
            </SrpButton>
            <span v-if="overrideUrlSetCount > 0" class="override-urls-link-wrap__count">({{ overrideUrlSetCount }} of {{ overrideUrlAdventures.length }} set)</span>
          </div>
          <div class="ui stackable grid" style="margin-top: 0">
            <div class="ui sixteen wide column">
              <div class="settings-actions">
                <SrpButton color="teal" size="small" @click="save()">Save</SrpButton>
                <SrpButton fill="outlined" color="gray" size="small" @click="copy()"> <b v-if="justCopied">Copied!</b><span v-else>Copy Code</span> </SrpButton>
                <h5 v-if="saveMessage" class="settings-actions__save-message">{{ saveMessage }}</h5>
                <button @click="askToRemoveList()" class="ui basic small button settings-actions__delete">Delete</button>
              </div>
              <p class="cms settings-actions__save-hint">Updates take effect immediately (no need to update your website code)</p>
            </div>
          </div>
        </div>

        <!--Including All-->
        <div v-if="selectedList.includeAllAdventures">
          <label class="cms-section-title">All Adventures</label>
          <div class="section-hints">
            <p v-if="Number(selectedList.maxCount) > 0">Max to Show is {{ selectedList.maxCount }}</p>
            <p v-if="selectedList.randomize">Adventures will be randomized</p>
          </div>
          <div class="ui four doubling raised link cards adventure-cards" style="margin-top: 7px">
            <ItineraryTile v-for="itinerary in itineraryListOriginal" :itinerary="itinerary" openNewWindow :allowShortImage="false" :source="null" :key="itinerary.id" />
          </div>
          <h4 v-if="itineraryListOriginal.length === 0">No adventures yet. As they're added, they will automatically show in your adventure wall!</h4>
        </div>

        <!--They want to select which adventures-->
        <AdventureSelector v-else :customerId="pageId" :adventureList="selectedList" :allAdventures="itineraryListMutable" :saveMessage="saveMessage" @save="save" @cancel="back" />

        <label class="cms-section-title" style="margin-bottom: 10px !important">The Code</label>
        <CodeSnippetWithSyntaxHighlighting>
          <template #line1
            ><i t>&lt;</i><i r>script</i> <i p>id</i>="shrpa-adv__{{ pageId }}--{{ listId }}" <i p>src</i>="https://shrpa.com/widget/adv.js" <i p>async</i><i t>&gt;</i><i t>&lt;/</i><i r>script</i
            ><i t>&gt;</i></template
          >
        </CodeSnippetWithSyntaxHighlighting>

        <label class="cms-section-title">What do I do with the code?</label>
        <p>
          Add it to your website (Wordpress, Simpleview, etc...)
          <br />
          Here's a full walkthrough!
          <br />
          <a style="text-decoration: underline" target="_blank" :href="globalGetLink('WebsiteIntegrations')"
            >{{ globalGetLink('WebsiteIntegrations') }}</a
          >
        </p>

        <br />
        <label class="cms-section-title">Questions?</label>
        <p>
          Having issues integrating this into your site? If you or your developer have any questions, we're happy to help! <br />
          Email us anytime at <CopyText />
        </p>
      </div>
    </div>

    <!--Delete Modal-->
    <SrpModal v-model:isVisible="showRemoveListModal">
      <template #header><h2 class="global-h2">Delete this Adventure Wall?</h2></template>
      <template #content>
        <NoteWithIcon color="red" icon="warning_2">
          <template #text>This Adventure Wall will no longer show on any web pages it was added to.</template>
        </NoteWithIcon>
      </template>
      <template #footer>
        <div class="ui basic black button" @click="cancelRemoveList()">Cancel</div>
        <div class="ui red button" @click="deleteList()">Yep, delete it.</div>
      </template>
    </SrpModal>

    <OverrideUrlsModal
      v-model:isVisible="showOverrideUrlsModal"
      :adventures="overrideUrlAdventures"
      :urlOverrides="selectedList?.urlOverrides ?? {}"
      :contentBaseUri="contentBaseUri"
      :isSaving="isSavingOverrideUrls"
      @save="saveUrlOverrides"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import axios from "axios";
import Loader from "@components/Loader/Loader.vue";
import { PageAdventureList } from "@contracts/pages";
import OrgContext from "@logic/OrgContext";
import AdminContext from "@logic/AdminContext";
import { PartnerNavInfo } from "@contracts/partnerNavInfo";
import AdventureSelector from "@components/AdventureSelector.vue";
import AdventureWallRepo from "@logic/AdventureWallRepo";
import ItineraryTile from "@components/ItineraryTile.vue";
import CopyText from "@components/CopyText.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import OverrideUrlsModal from "./OverrideUrlsModal.vue";
import { useHead } from "@unhead/vue";
import CodeSnippetWithSyntaxHighlighting from "@components/CodeSnippetWithSyntaxHighlighting.vue";
import { ItinerarySummary } from "@contracts/itinerary";

// NOTE: This does keep the in-memory lists in sync

export default defineComponent({
  name: "CustomIntegration",

  components: {
    CodeSnippetWithSyntaxHighlighting,
    SrpModal,
    NoteWithIcon,
    SrpButton,
    IconEmbedded,
    OverrideUrlsModal,
    Loader,
    ItineraryTile,
    AdventureSelector,
    CopyText,
  },

  data() {
    return {
      globalGetLink: inject("globalGetLink") as any,
      title: "Custom Adventure Wall",
      pageId: null as string,
      listId: null as string,
      itineraryListMutable: null as ItinerarySummary[],
      itineraryListOriginal: null as ItinerarySummary[],
      orgInContext: null as PartnerNavInfo,
      selectedList: null as PageAdventureList,
      code: "",
      justCopied: false,
      isLoading: false,
      // @ts-ignore
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri,
      isHotel: false,
      saveMessage: null as string | null,
      showRemoveListModal: false,
      showOverrideUrlsModal: false,
      isSavingOverrideUrls: false,
    };
  },

  computed: {
    overrideUrlAdventures(): ItinerarySummary[] {
      if (!this.selectedList) return [];
      const list = this.selectedList.includeAllAdventures ? this.itineraryListOriginal ?? [] : this.selectedList.adventures ?? [];
      return [...list].filter(a => !!a?.id).sort((a, b) => (a.title ?? "").localeCompare(b.title ?? "", undefined, { sensitivity: "base" }));
    },
    overrideUrlSetCount(): number {
      const overrides = this.selectedList?.urlOverrides ?? {};
      return this.overrideUrlAdventures.filter(a => !!(overrides[a.id!] ?? "").trim()).length;
    },
  },

  async mounted() {
    useHead({ title: () => this.title ?? "" });

    // @ts-ignore
    this.pageId = this.$route.params.pageId;
    await AdminContext.setOrgInContextFromCustomerId(this.pageId);
    // @ts-ignore
    this.listId = this.$route.params.listId;
    this.isHotel = OrgContext.isOrgInContextHotel(this);
    this.orgInContext = OrgContext.getOrgInContext(this);
    // Need to await these since we
    await this.loadList(this.listId);
    await this.loadCustomerPageDetails();
    this.removeSelectedItemsFromList();
  },

  methods: {
    async loadCustomerPageDetails() {
      let uri = `${import.meta.env.VITE_API_URL}/pages/${this.pageId}`;
      const response = await axios.get(uri);
      this.itineraryListOriginal = response.data.itineraries;
      this.itineraryListMutable = this.itineraryListOriginal.slice();
      this.title += ` - ${response.data.page.title}`;
    },
    async loadList(listId: string) {
      this.selectedList = await AdventureWallRepo.loadAdventureSummaryForList(this.pageId, listId);
    },
    removeSelectedItemsFromList() {
      if (!this.itineraryListMutable || !this.selectedList?.adventures || this.selectedList.adventures.length === 0) return;
      // Remove the items the user has selected from the "to pick from" list
      this.selectedList.adventures.forEach(selectedAdventure => {
        var index = this.itineraryListMutable.findIndex(i => i.id === selectedAdventure.id);
        if (index >= 0) this.itineraryListMutable.splice(index, 1);
      });
    },
    async save() {
      this.saveMessage = "Saving...";
      await AdventureWallRepo.updateList(this.pageId, this.selectedList);
      this.saveMessage = "Saved!";
      setTimeout(() => {
        this.saveMessage = null;
      }, 3000);
    },
    async saveUrlOverrides(urlOverrides: Record<string, string>) {
      if (!this.selectedList) return;
      this.selectedList.urlOverrides = urlOverrides;
      this.isSavingOverrideUrls = true;
      await this.save();
      this.isSavingOverrideUrls = false;
      this.showOverrideUrlsModal = false;
    },
    askToRemoveList() {
      this.showRemoveListModal = true;
    },
    cancelRemoveList() {
      this.showRemoveListModal = false;
    },
    async deleteList() {
      await AdventureWallRepo.deleteSiteIntegrationList(this.pageId, this.listId);
      this.back();
    },
    async copy() {
      // NOTE: Also in AdventureWidgets.vue
      let groupCode = this.listId ? `--${this.listId}` : "";
      // Note: Typescript just couldn't handle the /s in the string below so I threw in the unnecessary concat after fighting with it for too long
      let code = `<script id="shrpa-adv__${this.pageId}${groupCode}" src="https://shrpa.com/widget/adv.js" async></` + `script>`;
      await navigator.clipboard.writeText(code);

      this.justCopied = true;
      setTimeout(() => {
        this.justCopied = false;
      }, 5000);
    },
    back() {
      this.$router.push({ name: "AdventureWidgets", params: { pageId: this.pageId } });
    },
  },
});
</script>

<style scoped lang="scss">
.settings {
  margin-top: 1rem;
  padding: 12px 16px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
}
.settings-item-wrapper {
  .ui.toggle.checkbox {
    &.top {
      margin-bottom: 1rem;
    }
  }
}
.override-urls-link-wrap {
  margin: 4px 0 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  &__count {
    color: rgba(91, 91, 91, 1);
    font: 400 13px/18px sans-serif;
  }
}
.settings-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  &__save-message {
    margin: 0;
  }

  &__delete {
    margin-left: auto;
    flex-shrink: 0;
  }

  &__save-hint {
    margin: 6px 0 0;
  }
}

.section-hints {
  margin-top: 5px;

  p {
    margin: 0;
  }
}
</style>
